/**
 * HomeScreenPreference utility
 * Manages user's preference for which screen should be displayed as home
 * 
 * Routes:
 * - '/' = Charging Queue (MySessionPage)
 * - '/charger-table' = Chargers Table
 */

export type HomeScreenType = 'charging-queue' | 'chargers-table';

export interface HomeScreenPreference {
  screen: HomeScreenType;
  lastUpdated: string;
}

const STORAGE_KEY = 'chargequeue_home_screen_preference';

const DEFAULT_PREFERENCE: HomeScreenPreference = {
  screen: 'charging-queue',
  lastUpdated: new Date().toISOString(),
};

/**
 * Get the current home screen preference
 */
export function getHomeScreenPreference(): HomeScreenPreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to parse home screen preference:', error);
  }
  return { ...DEFAULT_PREFERENCE };
}

/**
 * Set the home screen preference
 */
export function setHomeScreenPreference(screen: HomeScreenType): HomeScreenPreference {
  const preference: HomeScreenPreference = {
    screen,
    lastUpdated: new Date().toISOString(),
  };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preference));
  } catch (error) {
    console.error('Failed to save home screen preference:', error);
  }

  return preference;
}

/**
 * Get the route path for the current home screen preference
 */
export function getHomeScreenRoute(preference?: HomeScreenPreference): string {
  const pref = preference || getHomeScreenPreference();

  switch (pref.screen) {
    case 'chargers-table':
      return '/charger-table';
    case 'charging-queue':
    default:
      return '/';
  }
}

/**
 * Reset all preferences (for testing or logout)
 */
export function resetHomeScreenPreference(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to reset home screen preference:', error);
  }
}
